import { Account as UserAccount } from '@ui/src';
import {
  GeneralRiskScoring,
  FeeItem,
  CustomerStatus,
  AccountLogs,
  Questionnarie,
  LinkedClient,
} from '../customers/types';
import { NatureOfControlEnum } from '../new-application/types';

export enum CorporateCustomerEntityForm {
  LTD = 'LTD',
  PLC = 'PLC',
  LP = 'LP',
  LLP = 'LLP',
}

export interface CorporateCustomerProps {
  name: string;
  type: CorporateCustomerEntityForm;
  registeredNumber: string;
  countryId: string;
  registeredAddress: string;
  businessAddress: string;
  incorporationDate: string;
  status: string;
  operationStatus: string;
  riskProfile: { totalRiskScore: number };
  id: string;
  render?: Record<string, React.JSX.Element>;
}

export type UserAccountProps = UserAccount & {
  visibleToClient: boolean;
  statusLogs: AccountLogs[];
  overdraftEnabled?: boolean;
};

export interface CorporateGeneralInfo {
  id?: string;
  name: string;
  registrationNumber: string;
  applicationId?: string;
  owner: { firstName: string; lastName: string };
  status?: CustomerStatus;
  statusChangeReason?: string;
  sic: string;
  countryOfIncorporated: string;
  totalBalance: number;
  currency: string;
  currencies: string[];
  cards: string[];
  accounts?: UserAccountProps[];
  risk: number;
  tariff?: string;
  tariffId?: string;
  tariffRiskScoreId?: string;
  fees?: FeeItem[];
  defaultFees?: FeeItem[];
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyTurnoverRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  merchantId?: string;
  vatNumber?: string;
  questionnaire?: Questionnarie;
  amlCtfComplianceProgram?: boolean | string | null;
  requiredCurrenciesCryptoId?: string[];
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  clientScheme?: { name: string; id: string };
  pspIntegrationOrganization?: Partial<LinkedClient>;
  officials?: {
    companyOfficialType: string;
    status: string;
    user: { profile: { firstName: string; lastName: string } };
  }[];
}

export interface CorporateOverviewGeneral {
  companyNumber: string;
  companyName: string;
  entityForm: CorporateCustomerEntityForm;
  dateOfIncorporation: string;
  countryOfIncorporationId: string;
  sicCodes: string[];
  mccCode: string;
  status: CustomerStatus;
  correspondentAddress: string;
  registrationAddress: string;
  businessAddress: string;
  websiteLink: string;
  businessDescription: string;
  correspondenceAddressSameAsRegisteredAddress: boolean;
  businessAddressSameAsRegisteredAddress: boolean;
  correspondenceCountryId?: string;
  correspondenceCity?: string;
  correspondencePostalCode?: string;
  correspondenceAddress1?: string;
  correspondenceAddress2?: string;
  businessCountryId?: string;
  businessCity?: string;
  businessPostalCode?: string;
  businessAddress1?: string;
  businessAddress2?: string;
  registeredCountryId?: string;
  registeredCity?: string;
  registeredPostalCode?: string;
  registeredAddress1?: string;
  registeredAddress2?: string;
  productIds: string[];
  merchantId?: string;
  vatNumber?: string;
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  requiredCurrenciesCryptoId?: string[];
  amlCtfComplianceProgram?: string | boolean;
  pspIntegrationOrganization?: Partial<LinkedClient>;
}

export enum SignatureType {
  NONE = 'NONE',
  SINGLE = 'SINGLE',
}

export enum AccessRight {
  NONE = 'NONE',
  VIEWER = 'VIEWER',
}

export interface CustomerCompanyOfficial {
  companyOfficialType: string;
  createdAt: string;
  deletedAt: string;
  id: string;
  natureOfControl?: NatureOfControlEnum;
  organizationId: string;
  percentage: string;
  position: string;
  status: string;
  signatureType: SignatureType;
  havingControl?: boolean;
  accessRight: AccessRight;
  user: {
    status: string;
    profile: { firstName: string; lastName: string; dob: string };
  };
  userId: string;
}

export interface CofFormValues {
  organizationId: string;
  firstName: string;
  lastName: string;
  dob: string;
  position?: string;
  percentage?: string;
  natureOfControl?: NatureOfControlEnum;
  havingControl?: boolean;
}

export interface AccountAccessCustomer {
  id: string;
  name: string;
  email: string;
  position: string;
  role: string;
  permissions?: string[];
  removeEnabled?: boolean;
  updated?: boolean;
  withError?: boolean;
}

export interface AmlBusinessPartner {
  id: string;
  name: string;
  volume: string;
  volumeCurrency: string;
}

export interface AmlBusinessPartners {
  suppliers: AmlBusinessPartner[];
  customers: AmlBusinessPartner[];
}

export type RiskScoring = GeneralRiskScoring;

export interface CorporateAmlGeneralInfo {
  entityForm: CorporateCustomerEntityForm;
  sic: string;
  mcc: string;
  description: string;
  nextReviewDate: string;
}

export interface NewCofFormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  dob: string;
  position: string;
  havingControl?: boolean;
  percentage?: string;
  natureOfControl?: string;
  signatureType?: string;
  companyOfficialType?: string;
  applicantId?: string;
  accessRight: AccessRight;
  applicationSchemeId?: string;
  nationalityId: string;
}
